import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Modal, Button, BREAKPOINTS } from '@awesomecomponents/mux';
import { BUTTON_VARIANT } from '@awesomecomponents/mux/core/components/Button';
import ErrorModal from './ErrorModal';
import { onRedirectAfterExit } from '../utils/modalFunctions';
import { bffRoutes } from '../utils/configuration';
import { appConstants } from '../utils/appConstants';
import useFetch from '../utils/useFetch';
import useInterval from '../utils/useInterval';
import { removeAuth } from '../utils/authFunctions';
import { useIdleTimer } from 'react-idle-timer';
import {
  isTimeBetweenBeginAndEndMaxActivity,
  isRefreshCookieCallSuccess,
  isTimePassedEndMaxActivity,
  isLogoutCallSuccess,
  isSaving,
  getModalBodyByAuth,
  getDisplayTimer,
  setTime,
} from '../utils/modalFunctions';
import HtmlReactParser from 'html-react-parser';

const ModalHeading = styled.p`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
  margin: 15px 0 30px 0;
  text-align: center;

  @media (max-width: ${BREAKPOINTS.MAX_768}px) {
    margin: 14px 0 20px 0;
    text-align: center;
  }
`;

const ModalContent = styled.p`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.5;
  margin-bottom: 30px;
  text-align: center;
  white-space: pre-wrap;

  @media (max-width: ${BREAKPOINTS.MAX_768}px) {
    margin: 0 0 30px 0;
    text-align: center;
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 0 0 5px 0;

  @media (max-width: ${BREAKPOINTS.MAX_768}px) {
    margin: 0;
  }
`;
const Time = styled.span`
  line-height: 4;
`;

const parseHtml = (html, time) => {
  return HtmlReactParser(html, {
    replace: (node) => {
      if (node.name === 'span') {
        return <Time>{time}</Time>;
      }
      return undefined;
    },
  });
};

const TimeoutModal = ({
  lang: language,
  langContent,
  isAuthenticated,
  setIsAuthenticated,
  children,
  IDLE_TIMER,
  IDLE_TIMER_REMAINING,
  INTERVAL_TIME,
  DEBOUNCE_TIME,
}) => {
  const {
    LOGOUT_ROUTE,
    PUBLIC_LOGOUT_ROUTE,
    TIMEOUT,
    MAX_ACTIVITY,
    MAX_ACTIVITY_LOGOUT,
    MAX_CIAM_ACTIVITY_LOGOUT,
    TIMEOUT_LOGOUT,
    SESSION_REFRESH_INTERVAL,
  } = appConstants;
  const history = useHistory();
  const [lang, setLangData] = useState(langContent[TIMEOUT]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMaxActivityReach, setIsMaxActivityReach] = useState(false);
  const [remainingTimeModal, setRemainingTimeModal] = useState(null);
  const [whichModalOpen, setWhichModalOpen] = useState(null);
  const [lastActive, setLastActive] = useState(null);
  const [isSessionTimeout, setIsSessionTimeout] = useState(false);

  useEffect(() => {
    setTime();
  }, []);

  const modalRef = useRef(null);
  const { HEADER, BUTTON_SIGNOUT, BUTTON_CANCEL } = lang;
  /**
   * use fetch hook to make logout request.
   */
  const { data, getData } = useFetch({ result: null, isLoading: false, error: null });

  const handleOnIdle = () => {
    //logout user.
    getData(bffRoutes.getLogout());
  };
  /**
   * set up idle timer with hook
   */
  const { getRemainingTime, reset, pause, getLastActiveTime } = useIdleTimer({
    timeout: IDLE_TIMER,
    onIdle: handleOnIdle,
    debounce: DEBOUNCE_TIME,
  });

  /**
   * Custom hook that will start the interval for 12 hours
   */
  const { seconds, resetTimer } = useInterval({ INTERVAL_TIME });

  /**
   * Now check the timer for max_activity
   * if seconds variable  passes 11 hour and 55 minutes, show modal that activity reach.
   * after 12 hours reach, logout the user.
   */
  useEffect(() => {
    if (isTimeBetweenBeginAndEndMaxActivity(seconds) && !isMaxActivityReach) {
      //open timeout modal and update lang state for max activity object.
      //pause idle timer
      pause();
      setLangData(langContent[MAX_ACTIVITY]);
      setIsMaxActivityReach(true);
      setIsModalOpen(true);
      setWhichModalOpen(MAX_ACTIVITY_LOGOUT);
    } else {
      if (isTimePassedEndMaxActivity(seconds) && isMaxActivityReach) {
        getData(bffRoutes.getLogout()); // logout the user
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  /**
   * handle idle timer effects here only.
   */
  useEffect(() => {
    //get remaining time from idle timer hook after every second.
    setLastActive(getLastActiveTime());
    const timer = setInterval(() => {
      if (getRemainingTime() < IDLE_TIMER_REMAINING) {
        setIsModalOpen(true);
        setRemainingTimeModal(getRemainingTime());
      }
    }, INTERVAL_TIME);
    return () => {
      clearInterval(timer);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCookie = async () => {
    const result = await fetch(bffRoutes.getRefreshCookie(), {
      mode: 'cors',
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': appConstants.ACCEPT_TYPE,
        Accept: appConstants.ACCEPT_TYPE,
      },
    });
    setIsSessionTimeout(!result.ok);
  };

  useEffect(() => {
    const timer = setInterval(async () => {
      const currentLastActive = getLastActiveTime();
      if (currentLastActive !== lastActive) {
        setLastActive(getLastActiveTime());
        refreshCookie();
      }
    }, SESSION_REFRESH_INTERVAL);
    return () => {
      clearInterval(timer);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastActive]);

  /**
   * reset the timer after refresh the cookie
   */
  useEffect(() => {
    if (isRefreshCookieCallSuccess(data)) {
      setIsModalOpen(false);
      reset();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  /**
   * //reset the max activity timer when user click on `keep me here` button
   */
  useEffect(() => {
    if (isRefreshCookieCallSuccess(data) && isMaxActivityReach) {
      resetTimer();
      setIsMaxActivityReach(false);
      setWhichModalOpen(null);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  /**
   * Set focus on modal for accessiblity.
   */
  useEffect(() => {
    isModalOpen && modalRef.current.focus();
  }, [isModalOpen]);

  /**
   * Hook will run after logout call is successful and redirect to logout page
   */
  useEffect(() => {
    let route = PUBLIC_LOGOUT_ROUTE;
    if (isLogoutCallSuccess(data)) {
      if (isAuthenticated) {
        removeAuth();
        setIsAuthenticated(false);
        route = LOGOUT_ROUTE;
      }

      let requestPage = whichModalOpen ?? TIMEOUT_LOGOUT;

      if (isMaxActivityReach && sessionStorage.hasOwnProperty('maxSessionExpirationTime')) {
        sessionStorage.removeItem('maxSessionExpirationTime');
        requestPage = MAX_CIAM_ACTIVITY_LOGOUT;
      }

      history.push(route, {
        requestPage,
        language,
      });
      setIsModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  return (
    <div ref={modalRef}>
      <Modal
        id="timeoutModal"
        data-testid="timeoutModal"
        isOpen={isModalOpen}
        showCloseButton={false}
        shouldCloseOnBackdropClick={false}
      >
        <ModalHeading>{HEADER}</ModalHeading>
        <ModalContent>
          {parseHtml(
            getModalBodyByAuth(isAuthenticated, lang),
            getDisplayTimer(isMaxActivityReach, remainingTimeModal, seconds),
          )}
        </ModalContent>
        {BUTTON_CANCEL && (
          <ButtonWrapper>
            <Button
              id="formhero-timeout-cancel"
              ariaLabel="close timeout modal"
              onClick={() => getData(bffRoutes.getRefreshCookie())}
              saving={isSaving(data, bffRoutes.getRefreshCookie())}
              disabled={isSaving(data, bffRoutes.getRefreshCookie())}
              variant={BUTTON_VARIANT.SECONDARY}
            >
              {BUTTON_CANCEL}
            </Button>
            <Button
              id="formhero-exitmodal-exit"
              ariaLabel="sign out from form hero"
              onClick={() => getData(bffRoutes.getLogout())}
              saving={isSaving(data, bffRoutes.getLogout())}
              disabled={isSaving(data, bffRoutes.getLogout())}
              disableClickEffect={true}
            >
              {BUTTON_SIGNOUT}
            </Button>
          </ButtonWrapper>
        )}
      </Modal>
      {isSessionTimeout && (
        <ErrorModal isErrorModalOpen={isSessionTimeout} lang={lang} onRedirectAfterExit={onRedirectAfterExit} />
      )}
      <div>{children}</div>
    </div>
  );
};

export default TimeoutModal;
